/* ==========================================================================
   EasyCool landing page behaviour.

   Deliberately dependency-free: it does not reach into the theme's internals,
   so a theme update cannot break it. The only integration point is the buy
   form, which stays a native <form is="product-form"> so the theme's own cart
   drawer keeps handling add-to-cart.
   ========================================================================== */

(function () {
  'use strict';

  // Four sections each request this file so they stay independently usable.
  // Browsers execute a duplicate <script src> more than once, so guard here.
  if (window.__ecLandingLoaded) return;
  window.__ecLandingLoaded = true;

  /* ------------------------------------------------------------ helpers -- */

  function readJSON(root, selector, fallback) {
    var el = root.querySelector(selector);
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return fallback;
    }
  }

  // Mirrors Shopify's money filter for the common format strings.
  function formatMoney(cents, format) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    format = format || '€{{amount_with_comma_separator}}';

    function withDelimiters(number, precision, thousands, decimal) {
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var decPart = parts[1] ? decimal + parts[1] : '';
      return intPart + decPart;
    }

    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    if (!match) return format;

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }

    return format.replace(placeholder, value);
  }

  function track(name, params) {
    if (typeof window.ecTrack === 'function') {
      window.ecTrack(name, params || {});
    } else if (typeof window.fbq === 'function') {
      window.fbq('track', name, params || {});
    }
  }

  /* ----------------------------------------------------------- gallery -- */

  if (!customElements.get('ec-gallery')) {
    customElements.define(
      'ec-gallery',
      class EcGallery extends HTMLElement {
        connectedCallback() {
          this.stage = this.querySelector('[data-ec-stage]');
          if (!this.stage) return;

          this.slides = Array.from(this.querySelectorAll('[data-ec-slide]'));
          this.thumbs = Array.from(this.querySelectorAll('[data-ec-thumb]'));
          this.prev = this.querySelector('[data-ec-prev]');
          this.next = this.querySelector('[data-ec-next]');
          this.index = 0;

          this.bind();
          this.updateArrows();
        }

        // First slide shows only the right arrow, last only the left.
        updateArrows() {
          var many = this.slides.length > 1;
          if (this.prev) this.prev.hidden = !many || this.index === 0;
          if (this.next) this.next.hidden = !many || this.index === this.slides.length - 1;
        }

        bind() {
          var self = this;

          if (this.prev) {
            this.prev.addEventListener('click', function () {
              self.goTo(self.index - 1);
            });
          }
          if (this.next) {
            this.next.addEventListener('click', function () {
              self.goTo(self.index + 1);
            });
          }

          this.thumbs.forEach(function (thumb) {
            thumb.addEventListener('click', function () {
              self.goTo(parseInt(thumb.dataset.ecThumb, 10));
            });
          });

          if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(
              function (entries) {
                entries.forEach(function (entry) {
                  if (!entry.isIntersecting) return;
                  var i = parseInt(entry.target.dataset.ecSlide, 10);
                  self.setActive(i);
                });
              },
              { root: this.stage, threshold: 0.6 }
            );
            this.slides.forEach(function (slide) {
              self.observer.observe(slide);
            });
          }
        }

        goTo(i) {
          var slide = this.slides[i];
          if (!slide) return;
          this.stage.scrollTo({ left: slide.offsetLeft - this.stage.offsetLeft, behavior: 'smooth' });
          this.setActive(i);
        }

        setActive(i) {
          if (i === this.index) return;
          this.index = i;
          this.updateArrows();
          this.thumbs.forEach(function (thumb) {
            thumb.classList.toggle('is-active', parseInt(thumb.dataset.ecThumb, 10) === i);
          });
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }
      }
    );
  }

  // True when the visitor arrived through an email discount link. Shopify only
  // reveals a discount at checkout, so without this the landing page shows the
  // full price to someone who has just been promised 150 EUR off.
  function discountLinkActive(marker) {
    marker = marker || 'dc';
    var key = 'ec-discount-active';
    var arrived = false;

    try {
      arrived = new URLSearchParams(window.location.search).has(marker);
    } catch (e) {
      arrived = window.location.search.indexOf(marker + '=') !== -1;
    }

    try {
      if (arrived) {
        window.sessionStorage.setItem(key, '1');
        return true;
      }
      return window.sessionStorage.getItem(key) === '1';
    } catch (e) {
      return arrived; // private mode: still honour the current page load
    }
  }

  /* --------------------------------------------- discount at checkout ---- */

  // Fallback for links that carry the dc marker but no code of their own.
  // Keep it in step with ec-subscribe-thanks.liquid.
  var EC_DISCOUNT_CODE = 'EASYCOOL150';

  // Whichever code the visitor actually arrived with wins, remembered for the
  // session. Hard coding one name here would have rewritten an influencer or
  // partner code to the newsletter code at the checkout step: the customer
  // would still pay the right price, but every one of those sales would be
  // credited to the wrong campaign.
  function ecActiveCode() {
    var key = 'ec-discount-code';
    try {
      var fromUrl = new URLSearchParams(window.location.search).get('discount');
      if (fromUrl) {
        window.sessionStorage.setItem(key, fromUrl);
        return fromUrl;
      }
      return window.sessionStorage.getItem(key) || EC_DISCOUNT_CODE;
    } catch (e) {
      return EC_DISCOUNT_CODE;
    }
  }

  // Shopify shows this discount in the cart on its own, so there is nothing for
  // the theme to draw. An earlier version rewrote the cart total here, from the
  // days when a code discount stayed invisible until checkout. Once Shopify
  // began showing it, that code was halving an already halved figure.
  function initDiscountCheckout() {
    if (!discountLinkActive('dc')) return;

    // Send the code with the navigation rather than trusting the cookie, which
    // the consent banner blocks until the visitor accepts.
    document.addEventListener('click', function (event) {
      var t = event.target;
      if (!t || !t.closest) return;
      if (!t.closest('[name="checkout"], a[href*="/checkout"]')) return;
      var code = ecActiveCode();
      if (!code) return;
      event.preventDefault();
      event.stopPropagation();
      window.location.href = '/checkout?discount=' + encodeURIComponent(code);
    }, true);
  }

  /* ------------------------------------------------ hero: variant logic -- */

  function initHero(root) {
    if (root.dataset.ecHeroReady === 'true') return;
    root.dataset.ecHeroReady = 'true';

    var variants = readJSON(root, '[data-ec-variants]', []) || [];
    var config = readJSON(root, '[data-ec-config]', {}) || {};

    var idInput = root.querySelector('[data-ec-variant-id]');
    var priceEl = root.querySelector('[data-ec-price]');
    var compareEl = root.querySelector('[data-ec-compare]');
    var saveEl = root.querySelector('[data-ec-save]');
    var buyBtn = root.querySelector('[data-ec-buy]');
    var buyLabel = root.querySelector('[data-ec-buy-label]');
    var stickyPrice = root.querySelector('[data-ec-sticky-price]');
    var stickyBar = root.querySelector('[data-ec-stickybar]');
    var stickyBtn = stickyBar ? stickyBar.querySelector('.ec-stickybar__btn') : null;
    var klarnaPlacement = root.querySelector('[data-ec-klarna-placement]');
    var klarnaWrap = root.querySelector('[data-ec-klarna]');

    var discountCents = parseInt(config.discountCents, 10) || 0;
    var discountOn = discountCents > 0 && discountLinkActive(config.discountMarker);

    var installFieldset = root.querySelector('.ec-install');
    var installRadios = Array.from(root.querySelectorAll('[data-ec-option-value]'));
    var optionSelects = Array.from(root.querySelectorAll('[data-ec-option-select]'));

    var installIndex = installFieldset ? parseInt(installFieldset.dataset.ecOptionIndex, 10) : -1;

    function currentOptions() {
      // Start from the variant currently in the hidden input so untouched
      // options keep their value even if they have no visible control.
      var current = variants.filter(function (v) {
        return String(v.id) === String(idInput && idInput.value);
      })[0] || variants[0];

      var opts = current && current.options ? current.options.slice() : [];

      if (installIndex >= 0) {
        var checked = installRadios.filter(function (r) { return r.checked; })[0];
        if (checked) opts[installIndex] = checked.value;
      }

      optionSelects.forEach(function (select) {
        var i = parseInt(select.dataset.ecOptionIndex, 10);
        if (!isNaN(i)) opts[i] = select.value;
      });

      return opts;
    }

    function findVariant(opts) {
      return variants.filter(function (v) {
        if (!v.options) return false;
        return v.options.every(function (val, i) {
          return String(val) === String(opts[i]);
        });
      })[0];
    }

    function updateCards() {
      installRadios.forEach(function (radio) {
        var card = radio.closest('[data-ec-install-card]');
        if (card) card.classList.toggle('is-selected', radio.checked);
      });
    }

    function markUnavailableCards() {
      if (installIndex < 0) return;
      installRadios.forEach(function (radio) {
        var opts = currentOptions();
        opts[installIndex] = radio.value;
        var variant = findVariant(opts);
        var card = radio.closest('[data-ec-install-card]');
        var unavailable = !variant || !variant.available;
        if (card) card.classList.toggle('is-unavailable', unavailable);
      });
    }

    function render(variant) {
      if (!variant) {
        if (buyBtn) buyBtn.disabled = true;
        if (stickyBtn) stickyBtn.disabled = true;
        if (buyLabel) buyLabel.textContent = config.unavailableLabel || 'Unavailable';
        return;
      }

      if (idInput) idInput.value = variant.id;

      // Arriving from a discount link: show the reduced price with the original
      // struck through. Otherwise fall back to the product's own compare-at.
      var showPrice = variant.price;
      var strikePrice = 0;
      var savedCents = 0;

      if (discountOn) {
        showPrice = Math.max(0, variant.price - discountCents);
        strikePrice = variant.price;
        savedCents = discountCents;
      } else if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        strikePrice = variant.compare_at_price;
        savedCents = variant.compare_at_price - variant.price;
      }

      var money = formatMoney(showPrice, config.moneyFormat);
      if (priceEl) priceEl.textContent = money;
      if (stickyPrice) stickyPrice.textContent = money;

      var hasStrike = strikePrice > 0;
      if (compareEl) {
        compareEl.classList.toggle('is-hidden', !hasStrike);
        if (hasStrike) compareEl.textContent = formatMoney(strikePrice, config.moneyFormat);
      }
      if (saveEl) {
        saveEl.classList.toggle('is-hidden', !hasStrike);
        if (hasStrike) {
          var saved = formatMoney(savedCents, config.moneyFormat);
          var tmpl = discountOn
            ? config.discountLabel || '{amount} off applied'
            : config.saveLabel || 'Save {amount}';
          saveEl.textContent = tmpl.replace('{amount}', saved);
        }
      }

      var available = variant.available !== false;
      if (buyBtn) buyBtn.disabled = !available;
      if (stickyBtn) stickyBtn.disabled = !available;
      if (buyLabel) {
        buyLabel.textContent = available
          ? config.buyLabel || 'Buy'
          : config.soldOutLabel || 'Sold out';
      }

      if (klarnaPlacement) {
        klarnaPlacement.setAttribute('data-purchase-amount', String(variant.price));
        try {
          if (window.Klarna && window.Klarna.OnsiteMessaging) {
            window.Klarna.OnsiteMessaging.refresh();
          }
        } catch (e) {}
      }

      // Keep the URL shareable and correct for back/forward navigation.
      if (config.productUrl && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    function sync() {
      updateCards();
      var variant = findVariant(currentOptions());
      render(variant);
      markUnavailableCards();
    }

    installRadios.forEach(function (radio) {
      radio.addEventListener('change', sync);
    });
    optionSelects.forEach(function (select) {
      select.addEventListener('change', sync);
    });

    updateCards();
    markUnavailableCards();

    /* --- Klarna: reveal the plain-text fallback if the widget never renders */
    if (klarnaWrap && klarnaPlacement) {
      var settle = function () {
        var rendered = klarnaPlacement.shadowRoot || klarnaPlacement.children.length > 0;
        klarnaWrap.classList.toggle('is-widget-loaded', !!rendered);
      };
      setTimeout(settle, 2500);
      setTimeout(settle, 6000);
    }

    /* --- sticky buy bar ------------------------------------------------- */
    if (stickyBar && buyBtn && 'IntersectionObserver' in window) {
      stickyBar.removeAttribute('hidden');
      var stickyObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            // Show the bar once the real button has scrolled past the top.
            var passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            stickyBar.classList.toggle('is-visible', passed);
          });
        },
        { threshold: 0 }
      );
      stickyObserver.observe(buyBtn);
    }

    /* --- require an installation choice, then track ---------------------- */
    var installRequired = installFieldset && installFieldset.hasAttribute('data-ec-install-required');
    var installError = root.querySelector('[data-ec-install-error]');

    function hasInstallChoice() {
      if (!installRequired) return true;
      return installRadios.some(function (r) { return r.checked; });
    }

    function promptForChoice() {
      if (installError) installError.hidden = false;
      if (installFieldset) {
        installFieldset.classList.add('is-missing');
        installFieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      var first = installRadios[0];
      if (first) first.focus({ preventScroll: true });
    }

    if (installRequired) {
      installRadios.forEach(function (radio) {
        radio.addEventListener('change', function () {
          if (installError) installError.hidden = true;
          if (installFieldset) installFieldset.classList.remove('is-missing');
        });
      });
    }

    // With a choice required, nothing is selected on load, so no variant
    // resolves and the price keeps whatever Liquid rendered: the discount only
    // appeared after the first tap on an installation card. Preview the price
    // from the first available variant so the struck through figure is there
    // immediately. Nothing is selected by this, and Buy still refuses to
    // proceed until a card is chosen.
    if (!installRadios.some(function (r) { return r.checked; })) {
      var preview = variants.filter(function (v) { return v && v.available; })[0] || variants[0];
      if (preview) {
        render(preview);
        // Clear the id so a preview can never be added to the cart by itself.
        if (installRequired && idInput) idInput.value = '';
        // Buy stays pressable so pressing it surfaces the choose-an-option error
        // rather than doing nothing at all.
        if (buyBtn) buyBtn.disabled = false;
        if (stickyBtn) stickyBtn.disabled = false;
      }
    }

    var form = root.querySelector('form.ec-form');
    if (form) {
      // Capture phase so this runs before the theme's product-form handler and
      // can stop the add-to-cart entirely.
      form.addEventListener(
        'submit',
        function (event) {
          if (!hasInstallChoice()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            promptForChoice();
            return;
          }

          var variant = findVariant(currentOptions());
          if (!variant) return;
          track('AddToCart', {
            content_ids: [String(variant.sku || variant.id)],
            content_type: 'product',
            value: variant.price / 100,
            currency: config.currency || 'EUR'
          });
        },
        true
      );
    }
  }

  /* --------------------------------------------------------- lead popup -- */

  if (!customElements.get('ec-lead-popup')) {
    customElements.define(
      'ec-lead-popup',
      class EcLeadPopup extends HTMLElement {
        connectedCallback() {
          this.config = readJSON(this, '[data-ec-pop-config]', {}) || {};
          this.storageKey = this.dataset.storageKey || 'ec-lead-popup-v1';
          this.designMode = this.dataset.designMode === 'true';
          this.showOnce = this.dataset.showOnce !== 'false';
          this.successMode = this.dataset.successMode || 'inline';
          this.successUrl = this.dataset.successUrl || '';
          this.alreadySubscribed = this.dataset.alreadySubscribed === 'true';
          // Server-rendered marker: Shopify redirected back here after a
          // successful no-JS form post.
          this.posted = !!this.querySelector('[data-ec-posted]');
          if (!this.posted) {
            // Shopify appends this when it redirects back from a customer form
            // post. Belt and braces for the case where the section renders
            // without the server-side marker.
            try {
              if (new URLSearchParams(window.location.search).get('customer_posted') === 'true' &&
                  window.sessionStorage.getItem('ec-lead-submitted') === '1') {
                this.posted = true;
              }
            } catch (e) {}
          }
          if (this.posted) {
            try { window.sessionStorage.removeItem('ec-lead-submitted'); } catch (e) {}
          }

          this.form = this.querySelector('form');
          this.emailInput = this.querySelector('input[type="email"]');
          this.consent = this.querySelector('[data-ec-consent]');
          this.consentLabel = this.consent ? this.consent.closest('.ec-pop__consent') : null;
          this.submit = this.querySelector('[data-ec-pop-submit]');
          this.submitLabel = this.querySelector('[data-ec-pop-submit-label]');
          this.errorEl = this.querySelector('[data-ec-pop-error]');
          this.offerState = this.querySelector('[data-ec-pop-state="offer"]');
          this.successState = this.querySelector('[data-ec-pop-state="success"]');

          this.bindClose();
          this.bindForm();

          if (this.posted) {
            // Shopify redirected back here after accepting the signup. succeed()
            // rather than showSuccess() so the Meta Lead event still fires; the
            // scripted path that used to fire it is gone.
            this.open();
            this.succeed();
            return;
          }

          this.schedule();
        }

        /* ------------------------------------------------------ scheduling */

        schedule() {
          var force = new URLSearchParams(window.location.search).has('ec_popup');

          if (this.designMode) {
            this.open();
            return;
          }

          // A logged-in customer who is already an email subscriber has used the
          // offer, never show it to them again, on any device.
          if (!force && this.alreadySubscribed) return;

          // Anybody who arrived on a discount link is already holding the offer:
          // through an influencer code, or one of our own emails. Asking them for
          // an email address in exchange for a voucher they can already see on
          // the page reads as a mistake, and it is one.
          if (!force && discountLinkActive('dc')) return;

          if (!force && this.isSuppressed()) return;

          // Same delay on every visit, however many times it has been dismissed.
          // Deliberate: the offer is the campaign, and a visitor who keeps coming
          // back without signing up is the one it most needs to reach. Only a
          // converted lead is exempt, and that is handled by isSuppressed above.
          var delay = (parseFloat(this.dataset.delay) || 6) * 1000;

          var self = this;
          this.shown = false;
          this.timer = setTimeout(function () { self.openOnce(); }, delay);

          // Scrolling is a better signal than elapsed time: it means somebody is
          // reading rather than sitting on a loaded tab.
          this.onScroll = function () {
            var el = document.documentElement;
            var max = (el.scrollHeight - el.clientHeight) || 1;
            var top = el.scrollTop || document.body.scrollTop || 0;
            if (top / max >= 0.35) self.openOnce();
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });

          // Exit intent, desktop only. Guarded by shown, so it catches people
          // leaving before the timer and never re-opens on someone who dismissed
          // it. Touch devices have no cursor, so there is nothing to detect.
          if (!window.matchMedia('(hover: none)').matches) {
            this.onExit = function (event) {
              if (event.clientY <= 0 && !event.relatedTarget) self.openOnce();
            };
            document.addEventListener('mouseout', this.onExit);
          }
        }

        // Every trigger routes through here, so whichever fires first wins and
        // the others are torn down.
        openOnce() {
          if (this.shown) return;
          this.shown = true;
          if (this.timer) clearTimeout(this.timer);
          if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
          if (this.onExit) document.removeEventListener('mouseout', this.onExit);
          this.open();
        }

        dismissCount() {
          try {
            return parseInt(window.localStorage.getItem(this.storageKey + '-d'), 10) || 0;
          } catch (e) {
            return 0;
          }
        }

        addDismiss() {
          try {
            window.localStorage.setItem(this.storageKey + '-d', String(this.dismissCount() + 1));
          } catch (e) {}
        }

        isSuppressed() {
          try {
            var raw = window.localStorage.getItem(this.storageKey);
            if (!raw) return false;
            var until = parseInt(raw, 10);
            if (!until || isNaN(until)) return false;

            // Anything more than 400 days out was written by the old
            // show-once-forever rule. Clear it, so every visitor locked out by
            // that behaviour becomes reachable again. A converted lead is only
            // ever suppressed for 365 days, so this cannot free one of those.
            if (until - Date.now() > 400 * 86400000) {
              window.localStorage.removeItem(this.storageKey);
              return false;
            }

            if (Date.now() > until) {
              window.localStorage.removeItem(this.storageKey);
              return false;
            }
            return true;
          } catch (e) {
            return false;
          }
        }

        suppress(days) {
          try {
            var span = (days || parseInt(this.dataset.expiryDays, 10) || 30) * 86400000;
            window.localStorage.setItem(this.storageKey, String(Date.now() + span));
          } catch (e) {}
        }

        markConverted() {
          // A converted lead should not see the offer again for a long while.
          this.suppress(365);
        }

        /* ---------------------------------------------------- open / close */

        open() {
          if (this.classList.contains('is-open')) return;

          this.removeAttribute('hidden');
          this.lastFocus = document.activeElement;
          // Next frame so the transition has a starting state to animate from.
          var self = this;
          requestAnimationFrame(function () {
            self.classList.add('is-open');
            document.documentElement.style.overflow = 'hidden';
            var focusTarget = self.successState && !self.successState.hasAttribute('hidden')
              ? self.querySelector('.ec-pop__done')
              : self.emailInput;
            if (focusTarget) focusTarget.focus({ preventScroll: true });
          });

          this.onKeydown = this.onKeydown || function (event) {
            if (event.key === 'Escape') self.close();
          };
          document.addEventListener('keydown', this.onKeydown);

          track('ViewContent', { content_name: 'EasyCool lead popup', content_type: 'popup' });
        }

        close() {
          if (this.timer) clearTimeout(this.timer);
          this.classList.remove('is-open');
          document.documentElement.style.overflow = '';
          if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);

          // A deliberate dismissal of the offer counts against the visitor's two
          // chances. Closing the thanks screen does not.
          if (this.offerState && !this.offerState.hasAttribute('hidden')) this.addDismiss();

          var self = this;
          setTimeout(function () {
            self.setAttribute('hidden', '');
            if (self.lastFocus && self.lastFocus.focus) self.lastFocus.focus({ preventScroll: true });
          }, 260);
        }

        bindClose() {
          var self = this;
          Array.from(this.querySelectorAll('[data-ec-pop-close]')).forEach(function (el) {
            el.addEventListener('click', function () {
              self.close();
            });
          });
        }

        /* ------------------------------------------------------------ form */

        showError(message) {
          if (!this.errorEl) return;
          this.errorEl.textContent = message;
          this.errorEl.hidden = !message;
        }

        showSuccess() {
          if (this.offerState) this.offerState.setAttribute('hidden', '');
          if (this.successState) this.successState.removeAttribute('hidden');
        }

        validate() {
          this.showError('');
          if (this.emailInput) this.emailInput.classList.remove('is-invalid');
          if (this.consentLabel) this.consentLabel.classList.remove('is-invalid');

          var email = this.emailInput ? this.emailInput.value.trim() : '';
          if (!email || !/^[^@\s]+@[^@\s.]+\.[^@\s]{2,}$/.test(email)) {
            if (this.emailInput) {
              this.emailInput.classList.add('is-invalid');
              this.emailInput.focus();
            }
            this.showError(this.config.invalidEmail || 'Please enter a valid email address.');
            return false;
          }

          if (this.consent && !this.consent.checked) {
            if (this.consentLabel) this.consentLabel.classList.add('is-invalid');
            this.showError(this.config.needConsent || 'Please confirm your consent to continue.');
            return false;
          }

          return true;
        }

        setBusy(busy) {
          if (this.submit) this.submit.disabled = busy;
          if (this.submitLabel) {
            this.submitLabel.textContent = busy
              ? this.config.sending || 'Sending…'
              : this.config.submitLabel || this.submitLabel.textContent;
          }
        }

        bindForm() {
          if (!this.form) return;
          var self = this;

          this.sending = false;

          // The submit must be a real one. Shopify's bot protection hooks the
          // form's native submit event to attach a CAPTCHA token, and rejects
          // anything posted without it. So this handler only validates and
          // cancels; it never sends.
          this.form.addEventListener('submit', function (event) {
            if (self.sending) { event.preventDefault(); return; }

            if (!self.validate()) {
              event.preventDefault();
              return;
            }

            // Remember the attempt. If Shopify returns us to a page where the
            // server-rendered success marker is missing, this is what still
            // shows the confirmation.
            try {
              window.sessionStorage.setItem('ec-lead-submitted', '1');
            } catch (e) {}

            self.sending = true;
            self.setBusy(true);
            // Nothing else: the browser posts the form from here.
          });

          // Ticking consent clears the warning. It must never send.
          if (this.consent) {
            this.consent.addEventListener('change', function (event) {
              event.stopPropagation();
              self.showError('');
              if (self.consentLabel) self.consentLabel.classList.remove('is-invalid');
            });
          }
        }

        succeed() {
          this.setBusy(false);
          this.markConverted();
          this.showSuccess();

          // One person is one lead. Shopify redirects back here after the form
          // post, and a reload of that page would otherwise fire Lead again,
          // inflating the count Meta optimises against.
          var tracked = false;
          try {
            tracked = window.localStorage.getItem('ec-lead-tracked') === '1';
            if (!tracked) window.localStorage.setItem('ec-lead-tracked', '1');
          } catch (e) {}

          if (!tracked) track('Lead', {
            content_name: 'EasyCool 50 percent voucher',
            content_category: 'landing-page-lead',
            currency: this.config.currency || 'EUR',
            value: this.config.leadValue || 0
          });

          // Optional thank-you page. The Lead event has already fired above;
          // the short delay just gives the pixel time to send before we navigate.
          if (this.successMode === 'redirect' && this.successUrl) {
            var url = this.successUrl;
            setTimeout(function () {
              window.location.href = url;
            }, 600);
            return;
          }

          var done = this.querySelector('.ec-pop__done');
          if (done) done.focus({ preventScroll: true });
        }

        disconnectedCallback() {
          if (this.timer) clearTimeout(this.timer);
          if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
          document.documentElement.style.overflow = '';
        }
      }
    );
  }

  /* ------------------------------------------- cookie widget cleanup ---- */

  // Consentmo floats a small round "cookie preferences" button bottom-left. On
  // phones it lands on top of the sticky buy bar and the installation cards.
  // We hide only that re-open button: it is a small fixed-position element.
  // The consent banner itself is wide, so the size test leaves it alone and
  // consent collection keeps working.
  function hideCookieWidget() {
    var host = document.querySelector('[data-ec-hide-cookie-widget]');
    if (!host) return;

    var PATTERN = /consentmo|gdpr|cookie/i;
    var MAX = 130; // px, anything larger is a banner rather than a button

    // Consent apps often render inside a shadow root, which a plain
    // querySelectorAll cannot see into. Walk through any shadow roots too.
    function deepAll(root, out) {
      out = out || [];
      var els;
      try {
        els = root.querySelectorAll('*');
      } catch (e) {
        return out;
      }
      for (var i = 0; i < els.length; i++) {
        out.push(els[i]);
        if (els[i].shadowRoot) deepAll(els[i].shadowRoot, out);
      }
      return out;
    }

    function sweep() {
      var found = 0;
      deepAll(document.body).forEach(function (el) {
        if (el.closest && el.closest('.ec-lp')) return;

        var id = el.id || '';
        var cls = typeof el.className === 'string' ? el.className : '';
        var label = el.getAttribute && (el.getAttribute('aria-label') || '');
        if (!PATTERN.test(id) && !PATTERN.test(cls) && !PATTERN.test(label || '')) return;

        var cs;
        try {
          cs = window.getComputedStyle(el);
        } catch (e) {
          return;
        }
        if (cs.position !== 'fixed') return;

        var r = el.getBoundingClientRect();
        if (!r.width || !r.height) return;
        if (r.width > MAX || r.height > MAX) return; // a banner, leave it

        el.style.setProperty('display', 'none', 'important');
        found++;
      });
      return found;
    }

    // Fallback: match by POSITION rather than by name. The consent app may use
    // class names we cannot predict, or render the button inside an iframe whose
    // contents are unreadable. What we can always measure is where it sits: a
    // small element, fixed, pinned to the bottom-left corner. Nothing else on
    // this page lives there, and our own sticky bar is excluded via .ec-lp.
    function sweepByPosition() {
      var vh = window.innerHeight;
      var found = 0;

      deepAll(document.body).forEach(function (el) {
        if (el.closest && el.closest('.ec-lp')) return;

        var cs;
        try {
          cs = window.getComputedStyle(el);
        } catch (e) {
          return;
        }
        if (cs.position !== 'fixed') return;
        if (cs.display === 'none' || cs.visibility === 'hidden') return;

        var r = el.getBoundingClientRect();
        if (!r.width || !r.height) return;
        if (r.width > 200 || r.height > 200) return; // a control, not a banner
        if (r.left > 200) return;                    // hugging the left edge
        if (vh - r.bottom > 250) return;             // near the bottom

        el.style.setProperty('display', 'none', 'important');
        found++;
      });
      return found;
    }

    function run() {
      sweep();
      sweepByPosition();
    }

    run();
    // The app injects late, so run again a few times rather than once.
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      run();
      if (tries >= 12) clearInterval(timer);
    }, 700);

    // And once more after everything has settled.
    window.addEventListener('load', function () {
      setTimeout(run, 400);
    });
  }

  /* ------------------------------------------------- discount banner ---- */

  function initDiscountBanner(banner) {
    if (banner.dataset.ecBannerReady === 'true') return;
    banner.dataset.ecBannerReady = 'true';

    var param = banner.dataset.param || 'dc';
    var storeKey = 'ec-discount-active';

    function reveal() {
      banner.removeAttribute('hidden');
    }

    if (banner.dataset.cartHasCode === 'true') {
      reveal();
      return;
    }

    var arrived = new URLSearchParams(window.location.search).has(param);
    var remembered = false;
    try {
      remembered = window.sessionStorage.getItem(storeKey) === '1';
    } catch (e) {}

    if (arrived) {
      try {
        window.sessionStorage.setItem(storeKey, '1');
      } catch (e) {}
      reveal();
    } else if (remembered) {
      reveal();
    }
  }

  /* --------------------------------------------- consultation bar tuck ---- */

  // Stays pinned while the visitor reads the top of the page, then slides away.
  // Without this it vanishes the instant you scroll; pinning it permanently
  // would collide with the theme's own scroll-up header.
  function initStickyBar() {
    var bar = document.querySelector('.ec-topbar--pinned, .ec-topbar--sticky');
    if (!bar || bar.dataset.ecStickyReady === 'true') return;
    bar.dataset.ecStickyReady = 'true';

    var distance = parseInt(bar.dataset.stickyDistance, 10);
    if (!distance || isNaN(distance)) distance = 2000;

    var pinned = bar.classList.contains('ec-topbar--pinned');

    if (pinned) {
      // Fixed, so it no longer occupies space. The spacer holds the gap, and the
      // same measurement drops the theme header so the two cannot overlap.
      // No ResizeObserver: the height only changes on a viewport resize, and an
      // observer that writes a custom property the layout reads back is a loop
      // waiting to happen.
      var spacer = document.createElement('div');
      spacer.className = 'ec-topbar__spacer';
      if (bar.parentNode) bar.parentNode.insertBefore(spacer, bar.nextSibling);

      document.documentElement.classList.add('ec-has-topbar');

      var measure = function () {
        var h = bar.offsetHeight || 0;
        if (h) document.documentElement.style.setProperty('--ec-topbar-h', h + 'px');
      };

      window.addEventListener('resize', measure, { passive: true });
      window.addEventListener('load', measure);
      measure();
    }

    var ticking = false;

    function update() {
      bar.classList.toggle('is-tucked', window.scrollY > distance);
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  // Diagnostic, only with ?ec_debug=1 on the URL. Reports which element is
  // actually on top at the tap point, whether the radios exist, and whether the
  // tap changed the selection. An invisible overlay and a dead handler look
  // identical from the outside; this tells them apart.
  function initClickDebug() {
    var on = false;
    try {
      on = new URLSearchParams(window.location.search).has('ec_debug');
    } catch (e) {}
    if (!on) return;

    window.addEventListener('error', function (e) {
      window.alert('EC UNCAUGHT | ' + (e.message || '') + ' | ' + (e.filename || '').split('/').pop() + ':' + e.lineno);
    });

    var shots = 0;

    document.addEventListener(
      'click',
      function (event) {
        if (shots >= 3) return;
        shots++;

        var top = document.elementFromPoint(event.clientX, event.clientY);
        var desc = function (el) {
          if (!el) return 'none';
          var cls = typeof el.className === 'string' ? el.className : '';
          return el.tagName.toLowerCase() + (el.id ? '#' + el.id : '') + (cls ? '.' + cls.split(/\s+/).slice(0, 2).join('.') : '');
        };

        var radios = document.querySelectorAll('[data-ec-option-value]');
        var checked = document.querySelectorAll('[data-ec-option-value]:checked');
        var fieldset = document.querySelector('.ec-install');
        var card = top && top.closest ? top.closest('[data-ec-install-card]') : null;

        window.setTimeout(function () {
          window.alert(
            'EC HIT' +
            ' | topmost: ' + desc(top) +
            ' | target: ' + desc(event.target) +
            ' | inside card: ' + (card ? 'yes' : 'no') +
            ' | radios: ' + radios.length +
            ' | checked: ' + document.querySelectorAll('[data-ec-option-value]:checked').length +
            ' | was: ' + checked.length +
            ' | fieldset: ' + (fieldset ? 'yes' : 'no') +
            ' | banner: ' + (document.querySelector('[data-ec-discount-banner]') ? 'yes' : 'no')
          );
        }, 60);
      },
      true
    );
  }

  /* ------------------------------------------------------------ bootstrap */

  // Safety net for when the custom element has not upgraded by the time boot
  // runs. Deleted by accident during the topbar work, which threw on every page
  // load and stopped every startup step that came after it.
  function initLeadPopupFallback() {
    var popup = document.querySelector('ec-lead-popup');
    if (!popup) return;
    if (typeof popup.bindForm !== 'function') return; // element never upgraded
    var form = popup.querySelector('form');
    if (!form || form.dataset.ecBound === 'true') return;
    popup.bindForm();
  }

  function boot() {
    initClickDebug();

    // Each step is isolated. Previously one throw anywhere in here stopped every
    // later step: a fault in the hero also took out the discount banner, which
    // is how two unrelated looking symptoms came from a single cause.
    var ecDebug = false;
    try {
      ecDebug = new URLSearchParams(window.location.search).has('ec_debug');
    } catch (e) {}

    function step(name, fn) {
      try {
        fn();
      } catch (err) {
        if (window.console && console.error) console.error('[ec] ' + name + ' failed', err);
        if (ecDebug) {
          window.alert('EC ERROR in ' + name + ' | ' + (err && err.message ? err.message : err) +
                       ' | ' + String(err && err.stack ? err.stack : '').split(String.fromCharCode(10)).slice(1, 3).join(' <- '));
        }
      }
    }

    step('stickyBar', function () { initStickyBar(); });
    step('leadPopupFallback', function () { initLeadPopupFallback(); });
    Array.from(document.querySelectorAll('.ec-hero')).forEach(function (el) {
      step('hero', function () { initHero(el); });
    });
    Array.from(document.querySelectorAll('[data-ec-discount-banner]')).forEach(function (el) {
      step('discountBanner', function () { initDiscountBanner(el); });
    });
    step('discountCheckout', function () { initDiscountCheckout(); });
    step('cookieWidget', function () { hideCookieWidget(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run when the theme editor swaps a section in.
  document.addEventListener('shopify:section:load', boot);
})();
